// A node's monitoring at a glance (DECISIONS 2026-10-10 "monitoring locked
// on a node"): who watches what, what it costs against the node's budget,
// and whether it is locked. Read by the machine's Monitoring tab, the
// machine row, the wall's header and the fleet's cards.
import type { Config, MonitorLock, MonitorStatus } from './api'
import { describeSource } from './monitor'

// who a viewer is, as the node's status names them: central's user, a
// location's relay ("relay <node id>"), or the node's own login ("")
export function whoLabel(viewer: string | undefined, nodeName: (id: string) => string = (id) => id): string {
    if (!viewer) return "the node's own login"
    const relay = viewer.match(/^relay (.+)$/)
    return relay ? `relay ${nodeName(relay[1]!)}` : viewer
}

export interface ViewerRow {
    who: string
    source: string // its name, and the tile's size when not the large view
    since: string
    route: string
    rtt: string
    connected: boolean
}

// one row per viewer, the longest watching first (the node's own order)
export function viewerRows(st: MonitorStatus | undefined, cfg: Config | undefined, nodeName?: (id: string) => string) {
    return (st?.viewers ?? []).map((v): ViewerRow => ({
        who: whoLabel(v.viewer, nodeName),
        source: sourceLabel(cfg, v.source),
        since: v.since,
        route: !v.connected ? 'connecting' : v.relay ? 'through TURN' : 'direct',
        rtt: v.rtt_ms > 0 ? `${v.rtt_ms.toFixed(0)} ms` : '—',
        connected: v.connected,
    }))
}

// a source key as a person reads it: "Cam 1 · 360p tile", a relayed one's
// as its origin's key (the relay has no config of it)
export function sourceLabel(cfg: Config | undefined, key: string): string {
    if (key.startsWith('relay/')) return key.split('/').slice(2).join('/') + ' (relayed)'
    const [base, size] = key.split('@')
    const name = describeSource(cfg, base!)?.name ?? base!
    return size ? `${name} · ${size}p tile` : name
}

// cores used of the budget, "~1.2 of 4 cores" (an older node: no budget)
export function coresText(st: MonitorStatus | undefined): string {
    const used = (st?.cpu_cores ?? 0).toFixed(1)
    return st?.budget_cores ? `~${used} of ${+st.budget_cores.toFixed(1)} cores` : `~${used} cores`
}

// the lock as a line: "by ops@example.com since 10 Oct 09:00"
export function lockText(l: MonitorLock): string {
    const d = new Date(l.since)
    const when = d.toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    })
    return `by ${l.by} since ${when}`
}

// the badge where the node is listed: locked (amber), or how many watch
// (nothing when nobody does)
export function monitorBadge(st: MonitorStatus | undefined): { text: string; tone: string; title: string } | null {
    if (st?.lock)
        return { text: 'monitoring locked', tone: 'text-warn', title: `Monitoring locked ${lockText(st.lock)}` }
    const n = st?.viewers.length ?? 0
    if (!n) return null
    return {
        text: `${n} watching`,
        tone: 'text-fg-2',
        title: `${n} viewer${n === 1 ? '' : 's'} on the node's monitoring, ${coresText(st)}`,
    }
}
